import {
  useDismissNotification,
  useAlertNotifications,
} from "features/notifications/hooks";
import type { AlertNotification } from "features/notifications/types";
import { AnimatePresence, motion } from "framer-motion";
import { Alert, Portal, styled, onGrid, useTheme } from "prism-react";
import type { FC } from "react";
import { useState } from "react";

const AlertAnimation: React.FC<
  AnimationProps & { layoutId: string; children: React.ReactNode }
> = ({ children, layoutId, ...props }) => {
  return (
    <motion.div
      animate={getAnimation(props)}
      exit={{ opacity: 0 }}
      initial={{
        opacity: 0,
        y: 35,
        scale: 0.98,
      }}
      layoutId={layoutId}
      style={{
        position: "absolute",
        bottom: 0,
        padding: onGrid(5), // makes sure that when you hover between toasts, it doesn't close
        paddingBottom: 0,
      }}
      layout
    >
      {children}
    </motion.div>
  );
};
const AlertsPosition = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "left",
  position: "fixed",
  bottom: "$4",
  left: 0,
  right: 0,
});

interface AnimationProps {
  hovering: boolean;
  itemNumber: number; // should be 1 indexed to match itemCount
  itemCount: number;
}

const getAnimation = ({ hovering, itemNumber, itemCount }: AnimationProps) => {
  if (hovering) {
    return {
      opacity: 1,
      y: -115 * (itemCount - itemNumber),
      scale: 1,
    };
  }

  const isTopToast = itemCount === itemNumber;
  const isUpperToast = itemNumber > itemCount - 3;
  const position = {
    y: -8 * (itemCount - itemNumber),
    scale: Math.max(1 - (itemCount - itemNumber) * 0.05, 0.8),
  };

  if (isUpperToast) {
    return {
      opacity: isTopToast ? 1 : 0.8,
      ...position,
    };
  }

  return {
    opacity: 0,
    ...position,
  };
};

const DismissableAlert: FC<AlertNotification> = (props) => {
  const dismiss = useDismissNotification();

  return (
    <Alert
      {...props}
      onDismiss={() => {
        props.callback?.();
        dismiss(props.id);
      }}
    />
  );
};

export const AlertStack: React.FC = () => {
  const notifications = useAlertNotifications();
  const [hovering, setHovering] = useState(false);

  const theme = useTheme();

  return (
    <Portal>
      <div className={theme.className}>
        <AlertsPosition
          onMouseEnter={() => {
            setHovering(true);
          }}
          onMouseLeave={() => {
            setHovering(false);
          }}
        >
          <AnimatePresence>
            {notifications.map((notification, index) => (
              <AlertAnimation
                key={notification.id}
                hovering={hovering}
                itemCount={notifications.length}
                itemNumber={index + 1}
                layoutId={notification.id}
              >
                <DismissableAlert {...notification} />
              </AlertAnimation>
            ))}
          </AnimatePresence>
        </AlertsPosition>
      </div>
    </Portal>
  );
};
